import * as React from 'react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@frontend/shadcn/components/ui/select';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { OFFER_PRICE_RULE_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import moment from 'moment';

import { Popover, PopoverTrigger, PopoverContent } from '@frontend/shadcn/components/ui/popover';
import { Button } from '@frontend/shadcn/components/ui/button';
import { format } from 'date-fns';
import { CalendarIcon } from '@revfluence/fresh-icons/solid/esm';
import PurchaseRestriction from '../../ShopifyPromoCodeSectionNew/FormElements/RefreshForm/AdditionalSettings/PurchaseRestrictions/PurchaseRestrictions';
import { IShopifyPromoCodeFormValues, IShopifyPromoCodeFormOptions, UpdateFieldAsyncActionPayload, TDisabledMap } from '../../../types';
import { Label } from '@/shadcn/components/ui/label';

interface DiscountBoostProps {
  discountBoostInput: Partial<IShopifyPromoCodeFormValues>;
  onChange: (val: Partial<IShopifyPromoCodeFormValues> | ((val: Partial<IShopifyPromoCodeFormValues>) => Partial<IShopifyPromoCodeFormValues>)) => void;
  formOptionsData?: IShopifyPromoCodeFormOptions;
  isSubscriptionEnable: boolean;
  disabledShopify: TDisabledMap<IShopifyPromoCodeFormValues>
}

export function DiscountBoost({ discountBoostInput: value, onChange, formOptionsData, isSubscriptionEnable, disabledShopify }: DiscountBoostProps) {
  // Adapter for reused components
  const handleBoostFormAsyncActions = ({ payload }: { action: string, payload: UpdateFieldAsyncActionPayload }) => {
    if (!payload?.key) return;
    onChange((prev) => ({
      ...prev,
      [payload.key]: payload.value,
    }));
  };

  // Validation for end date
  const [endDateError, setEndDateError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (value.activeDate && value.endDate && value.endDate.isBefore(value.activeDate)) {
      setEndDateError('End date must be after start date.');
    } else {
      setEndDateError(null);
    }
  }, [value?.activeDate, value?.endDate]);

  return (
    <div className="bg-grey-1 rounded-2xl flex flex-col gap-4 mt-4">
      {/* Discount Value */}
      <div>
        <Label className="text-primary">Discount Value</Label>
        <div className="flex items-center mt-2">
          <Select value={value.priceRuleType} onValueChange={(val) => handleBoostFormAsyncActions({ action: 'UPDATE_FIELD', payload: { key: 'priceRuleType', value: val as OFFER_PRICE_RULE_TYPE } })}>
            <SelectTrigger className="w-[132px] bg-grey-0 rounded-tr-none rounded-br-none border-r focus:border-r-grey-6">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999] bg-grey-0">
              <SelectItem value={OFFER_PRICE_RULE_TYPE.AMOUNT}>Amount Off</SelectItem>
              <SelectItem value={OFFER_PRICE_RULE_TYPE.PERCENTAGE}>Percent Off</SelectItem>
            </SelectContent>
          </Select>
          <InputWithAffix
            value={value.priceRuleAmount || 0}
            onChange={(e) => handleBoostFormAsyncActions({ action: 'UPDATE_FIELD', payload: { key: 'priceRuleAmount', value: Number(e.target.value).toString() } })}
            priceRuleType={value.priceRuleType}
          />
        </div>
      </div>
      {/* Discount Settings Accordion */}
      <Accordion type="multiple">
        <AccordionItem className="border-b-0" value="discount-settings">
          <AccordionTrigger className="border-b-0 text-base py-0 mt-2">Discount Settings</AccordionTrigger>
          <div className="border-t" />
          <AccordionContent>
            <PurchaseRestriction
              values={value}
              connectedSubscriptionEnabled={isSubscriptionEnable}
              connectedStore={new Set()}
              collections={formOptionsData?.productCollectionOptions || []}
              handleFormAsyncActions={handleBoostFormAsyncActions}
              isBoostOffer
              disabled={disabledShopify}
            />

            {/* Active Dates Accordion */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="active-dates">
                <AccordionTrigger className="text-sm font-medium py-0 mt-2">

                  <p className="text-grey-6 font-medium m-0">Discount Code Active Dates</p>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex gap-3">
                    <div className="py-2 flex flex-col gap-2">
                      <div className="flex gap-4">
                        <div>
                          <Label className="text-primary">Start Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-[240px] pl-3 text-left font-normal"
                              >
                                <span className="block text-sm mb-1">{value?.activeDate ? format((value?.activeDate).toDate(), 'MM/dd/yyyy') : 'Start Date'}</span>
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-grey-0 w-auto p-0 pointer-events-auto user-select-text" align="start">
                              <Calendar
                                mode="single"
                                selected={
                                  value?.activeDate
                                    ? value.activeDate.toDate()
                                    : undefined
                                }
                                onSelect={(date) => {
                                  handleBoostFormAsyncActions({ action: 'UPDATE_FIELD', payload: { key: 'activeDate', value: moment(date) || null } });
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-primary">End Date (optional)</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-[240px] pl-3 text-left font-normal"
                              >
                                <span className="block text-sm mb-1">{value.endDate ? format((value.endDate).toDate(), 'MM/dd/yyyy') : 'End Date'}</span>
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-grey-0 w-auto p-0 pointer-events-auto user-select-text" align="start">
                              <Calendar
                                mode="single"
                                selected={
                                  value.endDate
                                    ? value.endDate.toDate()
                                    : undefined
                                }
                                onSelect={(date) => handleBoostFormAsyncActions({ action: 'UPDATE_FIELD', payload: { key: 'endDate', value: moment(date) || null } })}
                              />
                            </PopoverContent>
                          </Popover>
                          {endDateError && (
                            <span className="text-red-500 text-xs mt-1 block">{endDateError}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}

function InputWithAffix({
  value,
  onChange,
  priceRuleType,
}: {
  value: string | number;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  priceRuleType: OFFER_PRICE_RULE_TYPE;
}) {
  return (
    <div className="relative w-[200px]">
      {priceRuleType === OFFER_PRICE_RULE_TYPE.AMOUNT && (
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-grey-6">$</span>
      )}
      <Input
        type="number"
        min={1}
        value={value}
        onChange={onChange}
        className={`w-full bg-grey-0 border-l-0 rounded-tl-none rounded-bl-none ${priceRuleType === OFFER_PRICE_RULE_TYPE.AMOUNT ? 'pl-6' : ''} ${priceRuleType === OFFER_PRICE_RULE_TYPE.PERCENTAGE ? 'pr-6' : ''}`}
        placeholder=""
      />
      {priceRuleType === OFFER_PRICE_RULE_TYPE.PERCENTAGE && (
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-grey-6">%</span>
      )}
    </div>
  );
}
